<template>
  <ProjectScaffold :loading="loadingAssets">
    <div class="tabs">
      <TabsNavigation :tab-definitions="tabDefinitions" v-model="selectedTabId" />
      <TabsContent :tab-definitions="tabDefinitions" v-model="selectedTabId" class="tabs-content">
        <div v-if="selectedTabId === 'project-assets'">
          <div class="header">
            <div class="titles">
              <div class="title">Project assets</div>
              <div class="subtitle">
                All the assets uploaded by your clients for <b>{{ currentProjectName }}</b> project are stored on Google
                Drive.
              </div>
            </div>
          </div>
          <ts-divider></ts-divider>
          <div class="flex items-center justify-center drive-container">
            <Drive />
            <ts-button type="primary" size="large" filled icon="el-icon-plus" @click="goToProjectDrive">
              Go to my assets on Google Drive
            </ts-button>
          </div>
        </div>
        <div v-if="selectedTabId === 'file-requests'">
          <div class="header">
            <div class="titles">
              <div class="title">File requests</div>
              <div class="subtitle">
                <template v-if="isProjectManager">
                  Enables your clients to directly submit assets to the project without having to log in
                </template>
                <template v-else
                  >Enables your agency or member of your team to directly submit assets to the project on our platform
                  without having to log in
                </template>
              </div>
            </div>
            <div class="actions flex-row items-end">
              <CreateFileRequest @input="handleFileRequestAdded" class="mb-4" />
              <div class="">
                <FilterPicker :value="currentFileRequestFilter" @input="handleFileRequestFilterSelectChange" />
              </div>
            </div>
          </div>
          <div class="requests-tabs">
            <ul class="list-reset flex">
              <li class="requests-tabs-item" :class="{ active: !showArchivedRequests }">
                <a href="#" class="" @click.prevent="showArchivedRequests = false"> Current requests </a>
                <span class="divider"></span>
              </li>
              <li class="requests-tabs-item" :class="{ active: showArchivedRequests }">
                <a href="#" class="" @click.prevent="showArchivedRequests = true"> Closed requests </a>
                <span class="divider"></span>
              </li>
            </ul>
          </div>
          <ts-divider></ts-divider>
          <file-request-list
            label="File requests"
            :file-requests="filteredFileRequests"
            @remove="handleFileRequestDelete"
            @update="handleFileRequestUpdate"
          ></file-request-list>
        </div>
        <div class="" v-if="selectedTabId === 'upload-assets'">
          <div class="header">
            <div class="titles">
              <div class="title">Upload your assets</div>
              <div class="subtitle">Drag your files below and press upload to approve the transfer.</div>
            </div>
          </div>
          <ts-divider></ts-divider>
          <div class="items-center mb-4">
            <file-request-uploader
              :resumable="resumable"
              :files="uploadingAssets"
              label=""
              group="campaign-assets"
              @added="handleAddUploadingAssets"
              @removed="handleRemoveUploadingAssets"
            />
          </div>
          <div class="flex items-center justify-center" v-if="hasAssets">
            <ts-button type="primary" size="medium" :loading="uploading" filled @click.prevent="uploadAssets">
              Upload
            </ts-button>
          </div>
        </div>
        <div class="" v-if="selectedTabId === 'upload-history'">
          <div class="header">
            <div class="titles">
              <div class="title">Upload history</div>
            </div>
          </div>
          <ts-divider></ts-divider>
          <div class="">
            <div class="uploads-table">
              <div class="table-header">
                <div class="">
                  <div class="">#</div>
                </div>
                <div class="">
                  <div class="">Date and hour</div>
                </div>
                <div class="" v-if="!isOnlyCustomer">
                  <div class="">Go to folder</div>
                </div>
                <div class="">
                  <div class="">Number of files</div>
                </div>
              </div>
              <section class="table-content">
                <div class="" v-for="(upload, index) in uploads" :key="index">
                  <div class="">
                    <div class="">
                      {{ index + 1 }}
                    </div>
                  </div>
                  <div class="">
                    <div class="">
                      {{ formatTo(upload.uploadedAt) }}
                    </div>
                  </div>
                  <div class="" v-if="!isOnlyCustomer">
                    <div class="">
                      <a
                        v-if="upload.public_url"
                        :href="upload.public_url"
                        target="blank"
                        style="text-decoration: underline"
                        >{{ upload.folder }}</a
                      >
                    </div>
                  </div>
                  <div class="">
                    <div class="" v-if="upload.completed">
                      {{ upload.assets.length }}
                    </div>
                    <div class="flex items-center justify-between" v-else>
                      <p class="">Uploading...</p>
                      <p class="">{{ upload.assets.length }}/{{ upload.total }}</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </TabsContent>
    </div>
  </ProjectScaffold>
</template>

<style lang="scss" scoped>
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.drive-container {
  flex-direction: column;
  & > * {
    margin: 10px;
  }
}
.uploads-table {
  width: 100%;
  & .table-header {
    display: flex;
    margin-bottom: 1rem;
    & > div {
      width: 25%;
      font-family: Roboto;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.4;
      text-align: left;
      letter-spacing: normal;
      color: var(--fills-neutral-grey-medium);
      padding: 0rem 0.5rem;
    }
    & > div:first-child {
      width: initial;
      padding-right: 2.5rem;
      flex: inherit;
    }
  }
  & .table-content {
    & > div {
      display: flex;
      border-radius: 16px;
      margin-bottom: 0.5rem;
      border: solid 2px rgba(255, 255, 255, 0.16);
      background-color: #ebecf0;
      & > div {
        width: 25%;
        padding: 0.5rem 0.5rem;
        font-family: Roboto;
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.4;
        text-align: left;
        letter-spacing: normal;
        color: var(--fills-neutral-grey-medium);
      }
      & > div:first-child {
        width: initial;
        flex: inherit;
        padding-right: 2.5rem;
      }
    }
  }
}
.hidden {
  display: none;
}
.flex-row {
  flex-direction: column;
}
.mr-4 {
  margin-right: 1rem;
}
.items-end {
  align-items: flex-end;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}
.flex {
  display: flex;
}
.list-reset {
  list-style: none;
  margin: 0;
}
.requests-tabs {
  margin-bottom: -1.6em;
}
.requests-tabs-item {
  &:hover .divider,
  &.active .divider {
    position: absolute;
    bottom: 0;
    left: 0;
    background: black;
    height: 3px;
    width: 100%;
    z-index: 999;
  }
  position: relative;
  display: block;
  & a {
    padding: 1em 1em;
    display: block;
  }
}
a {
  text-decoration: none;
  color: #6d7587;
}
.tabs {
  display: flex;

  .tabs-content {
    flex-grow: 1;
    padding: 40px 46px;
  }
}

.header {
  display: flex;

  .titles {
    flex-grow: 1;
  }

  .title {
    font-family: Roboto;
    font-size: 20px;
    color: #6d7587;
  }

  .subtitle {
    font-family: Roboto;
    font-size: 12px;
    color: #6d7587;

    margin-top: 12px;
  }

  .actions {
    flex-shrink: 0;
    margin-left: 50px;
    display: flex;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import Drive from '@/assets/drive.svg';
import { mapState, mapActions } from 'vuex';
import { orderBy } from 'lodash';
import moment from 'moment';
import { formatDate } from '@/api/workflow';

import { downloadProjectAssets, createUploadRecord, updateUploadRecord } from '@/api/workflow';

import ProjectScaffold from '../ProjectScaffold.vue';
import AssetList from './AssetList.vue';
import FilterPicker from '@/components/inputs/FilterPicker.vue';
import FileRequestList from './FileRequestList.vue';
import CreateAsset from './CreateAsset.vue';
import CreateFileRequest from './CreateFileRequest.vue';
import AssetPicker from './AssetPicker.vue';
import TabsNavigation from '@/components/TabsNavigation.vue';
import TabsContent from '@/components/TabsContent.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';
import FileRequestUploader from './FileRequestUploader.vue';
import { format } from 'date-fns';

const removeExtension = value => {
  const reg = new RegExp(/(\.\w*$)/);
  return isNaN(value) ? value.replace(reg, '') : parseInt(value);
};

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    ProjectScaffold,
    Drive,
    AssetList,
    FileRequestList,
    TabsNavigation,
    TabsContent,
    CreateAsset,
    CreateFileRequest,
    FilterPicker,
    AssetPicker,
    FileRequestUploader
  },
  data() {
    return {
      selectedTabId: null,
      uploadingAssets: [],
      currentUpload: null,
      workingFileFilter: 'latest',
      campaignAssetFilter: 'latest',
      brandAssetFilter: 'latest',
      fileRequestFilter: 'latest',
      filterOptions: {
        latest: { field: 'createdAt', order: 'asc', handler: item => moment(item.createdAt).unix() },
        oldest: { field: 'createdAt', order: 'desc', handler: item => moment(item.createdAt).unix() },
        asc: {
          field: 'name',
          order: 'asc',
          handler(asset) {
            const name = removeExtension(asset.name);
            return isNaN(name) ? name.toLowerCase() : parseInt(name);
          }
        },
        desc: {
          field: 'name',
          order: 'desc',
          handler(asset) {
            const name = removeExtension(asset.name);
            return isNaN(name) ? name.toLowerCase() : parseInt(name);
          }
        }
      },
      showArchivedRequests: false
    };
  },
  computed: {
    ...mapState('workflow', {
      currentProjectId: state => state.currentProjectId + '',
      currentProject: state => state.currentProject.value
    }),
    ...mapState('workflow/assets', {
      loadingAssets: state => state.assets.loading,
      uploading: state => state.uploads.uploading,
      assets: state => state.assets.items,
      uploads: state => state.uploads.items,
      fileRequests: state => state.fileRequests.items,
      assetsError: state => state.assets.error
    }),
    ...mapState('workflow/fileRequests', {
      loadingFileRequests: state => state.fileRequests.loading,
      fileRequests: state => state.fileRequests.items,
      fileRequestsError: state => state.fileRequests.error
    }),
    currentProjectName() {
      return this.currentProject?.name;
    },
    currentCampaignAssetFilter() {
      return this.campaignAssetFilter;
    },
    currentBrandAssetFilter() {
      return this.brandAssetFilter;
    },
    currentFileRequestFilter() {
      return this.fileRequestFilter;
    },
    filteredFileRequests() {
      const { field, order, handler } = this.filterOptions[this.currentFileRequestFilter];
      const fileRequests = orderBy(this.fileRequests, [asset => (handler ? handler(asset) : asset[field])], [order]);

      return fileRequests.filter(item => item.archived === this.showArchivedRequests);
    },
    tabs() {
      return [
        {
          id: 'project-assets',
          name: 'Project assets',
          visible: this.isProjectManager || this.isCreator
        },
        {
          id: 'upload-assets',
          name: 'Upload your assets',
          visible: this.isCustomer
        },
        {
          id: 'upload-history',
          name: 'Upload history',
          visible: this.isProjectManager || this.isCustomer
        },
        {
          id: 'file-requests',
          name: 'File requests',
          visible: this.isProjectManager
        }
      ];
    },
    tabDefinitions() {
      return this.tabs.filter(tab => tab.visible);
    },
    campaignAssets() {
      const assets = [
        ...this.assets.filter(asset => asset.group === 'campaign-assets'),
        ...this.uploadingAssets.filter(asset => asset.group === 'campaign-assets')
      ];

      const { field, order, handler } = this.filterOptions[this.currentCampaignAssetFilter];
      return orderBy(assets, [asset => (handler ? handler(asset) : asset[field])], [order]);
    },
    brandIdentityAssets() {
      const assets = [
        ...this.assets.filter(asset => asset.group === 'brand-identity'),
        ...this.uploadingAssets.filter(asset => asset.group === 'brand-identity')
      ];
      const { field, order, handler } = this.filterOptions[this.currentBrandAssetFilter];
      return orderBy(assets, [asset => (handler ? handler(asset) : asset[field])], [order]);
    },
    hasAssets() {
      return this.uploadingAssets.length > 0;
    }
  },
  methods: {
    ...mapActions('workflow/assets', [
      'fetchCurrentProjectAssets',
      'fetchCurrentProjectUploads',
      'addCurrentProjectAsset',
      'updateCurrentProjectAsset',
      'removeCurrentProjectAsset'
    ]),
    ...mapActions('workflow/fileRequests', [
      'fetchCurrentProjectFileRequests',
      'addCurrentProjectFileRequest',
      'updateCurrentProjectFileRequest',
      'removeCurrentProjectFileRequest'
    ]),
    formatTo(date) {
      return format(new Date(date * 1000), 'dd.MM.yyyy - HH:mm');
    },
    goToProjectDrive() {
      if (!this.currentProject.googleDriveUrl) return;
      window.open(decodeURI(this.currentProject.googleDriveUrl));
    },
    toDateString(date) {
      return formatDate(date);
    },
    handleAddUploadingAssets(asset) {
      this.uploadingAssets.push(asset);
    },
    handleRemoveUploadingAssets(asset) {
      this.uploadingAssets = this.uploadingAssets.filter(item => {
        return asset.resumableFile.uniqueIdentifier !== item.resumableFile.uniqueIdentifier;
      });

      this.resumable.removeFile(asset.resumableFile);
    },
    async uploadAssets() {
      if (!this.hasAssets) {
        return;
      }
      // start upload
      this.startUpload = await createUploadRecord({
        projectId: this.currentProjectId
      });
      this.resumable.upload();
    },
    handleProjectAssetsAdded(assets) {
      assets.forEach(asset => {
        this.addCurrentProjectAsset({
          asset: {
            ...asset,
            fileId: asset.file.id
          }
        });
      });
    },
    handleCampaignAssetFilterSelectChange(value) {
      this.campaignAssetFilter = value;
    },
    handleBrandAssetFilterSelectChange(value) {
      this.brandAssetFilter = value;
    },
    handleFileRequestFilterSelectChange(value) {
      this.fileRequestFilter = value;
    },
    async handleAssetAdded(asset) {
      if (asset.type === 'uploaded-file') {
        this.uploadingAssets = [
          ...this.uploadingAssets,
          {
            ...asset,
            id: asset.resumableFile.uniqueIdentifier,
            group: this.selectedTabId,
            createdAt: new Date().toISOString(),
            createdBy: this.currentUser
          }
        ];
        this.resumable.upload();
      } else {
        try {
          await this.addCurrentProjectAsset({
            asset: {
              ...asset,
              group: this.selectedTabId
            }
          });
        } catch (err) {
          asset.uploadError = true;
          asset.uploadErrorMessage = err.message;
        }
      }
    },
    handleFileAdded(group, file) {
      this.uploadingAssets = [
        ...this.uploadingAssets,
        {
          id: file.uniqueIdentifier,
          name: file.fileName,
          group,
          type: 'uploaded-file',
          resumableFile: file,
          createdAt: new Date().toISOString(),
          createdBy: this.currentUser,
          uploadError: false,
          uploadErrorMessage: ''
        }
      ];
    },
    async handleFileSuccess(file) {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        try {
          const createdAsset = await this.addCurrentProjectAsset({ asset });
          this.startUpload.assets.push(createdAsset.id);
          this.uploadingAssets = this.uploadingAssets.filter(
            a => a.resumableFile.uniqueIdentifier !== file.uniqueIdentifier
          );
          if (this.uploadingAssets.length == 0) {
            await updateUploadRecord(this.startUpload);
            this.currentUpload = null;
            window.EventBus.$emit('download.pending', 'Your files have been uploaded ! We will let your producer know');
          }
        } catch (err) {
          console.error('Server error when adding asset', err);
          asset.uploadError = true;
          asset.uploadErrorMessage = err.message;
          this.showError('Error when adding asset');
        }
      }
    },
    handleFileError(file, message) {
      const asset = this.uploadingAssets.find(a => a.resumableFile.uniqueIdentifier == file.uniqueIdentifier);
      if (asset) {
        console.error('Server error when adding asset', message);
        asset.uploadError = true;
        asset.uploadErrorMessage = message;
        this.showError('Error when adding asset');
      }
    },
    handleAssetEdit(asset) {
      this.updateCurrentProjectAsset({ asset });
    },
    handleAssetDelete(asset) {
      if (!asset.resumableFile) {
        this.removeCurrentProjectAsset({ asset });

        return;
      }

      this.uploadingAssets = this.uploadingAssets.filter(item => {
        return item.resumableFile.uniqueIdentifier != asset.resumableFile.uniqueIdentifier;
      });
    },
    handleAssetsDownload(assets) {
      this.downloadFile(
        downloadProjectAssets({
          projectId: this.currentProjectId,
          assetIds: assets.map(a => a.id)
        }),
        `ts_assets_project${this.currentProjectId}.zip`
      );
    },
    handleFileRequestAdded(fileRequest) {
      this.addCurrentProjectFileRequest({ fileRequest });
    },
    handleFileRequestDelete(fileRequest) {
      this.removeCurrentProjectFileRequest({ fileRequest });
    },
    handleFileRequestUpdate(fileRequest) {
      this.updateCurrentProjectFileRequest({ fileRequest });
    },
    selectTab() {
      return this.tabs.find(t => t.visible).id;
    }
  },
  async mounted() {
    this.selectedTabId = this.selectTab();
    this.fetchCurrentProjectUploads();
    this.fetchCurrentProjectAssets();
    this.fetchCurrentProjectFileRequests('assets');
    this.resumable.on('fileSuccess', file => {
      this.handleFileSuccess(file);
    });
    this.resumable.on('fileError', (file, message) => {
      this.handleFileError(file, message);
    });
  },
  watch: {
    assetsError(value) {
      if (value) {
        this.showError(value);
      }
    }
  }
};
</script>
